"use client";

import React, { useState } from "react";
import { BookOpen, CircleCheck as CheckCircle2, ExternalLink, Sparkles, Youtube } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const takeaways = [{
  title: "Biblical Principles for Daily Direction",
  description: "Discover timeless scriptural foundations designed to anchor your heart and guide daily decisions."
}, {
  title: "Overcome Seasons of Uncertainty",
  description: "Navigate complex life challenges with resilient faith, spiritual clarity, and pastoral encouragement."
}, {
  title: "Cultivate a Lasting Spiritual Legacy",
  description: "Build enduring, faith-centered habits that inspire your family and impact future generations."
}];
export default function Bookhero() {
  const [isExpanded, setIsExpanded] = useState(false);
  const featureTags = [{
    label: "Faith-Centered"
  }, {
    label: "Practical Application"
  }];
  return <section id="bookhero" data-nav="light" className="relative overflow-hidden bg-background py-10 md:py-14 lg:py-16 text-foreground">
      {/* Ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -z-10 hidden md:block h-96 w-full -translate-x-1/2 max-w-7xl rounded-lg bg-accent/15 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-10 -z-10 hidden md:block h-72 w-72 rounded-lg bg-card/60 blur-2xl" />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12">
          
          {/* Book Cover Card Column (Left on Desktop, First on Mobile) */}
          <div className="order-1 lg:order-1 flex justify-center lg:col-span-5">
            <div className="group relative w-full max-w-xs sm:max-w-sm lg:max-w-md">
              {/* Backing glow behind book */}
              <div aria-hidden="true" className="absolute -inset-2 rounded-lg bg-accent/20 blur-xl transition-opacity duration-500 group-hover:opacity-100" />
              
              {/* Book container */}
              <div className="relative overflow-hidden rounded-lg border border-border bg-card p-3 shadow-2xl sm:p-4">
                <AspectRatio ratio={2 / 3} className="overflow-hidden rounded-lg bg-muted/40">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/RootedCover_FullNew311.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/RootedCover_FullNew311.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/RootedCover_FullNew311.jpg-wvc-sizes" alt="Rooted In Purpose by Dustin Royer - Book Cover" loading="lazy" decoding="async" className="h-full w-full object-contain object-center shadow-inner transition-transform duration-500 ease-out group-hover:scale-[1.02]" />
                </AspectRatio>

                {/* Footnote bar */}
                <div className="mt-3 flex items-center justify-between px-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium">
                    <BookOpen className="size-3.5 text-accent" />
                    Paperback &amp; Kindle
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex size-7 items-center justify-center rounded-md bg-accent/20 text-accent-foreground">
                      <BookOpen className="size-4 text-accent" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground">Dustin Royer</p>
                      <p className="text-[11px] text-muted-foreground">Author &amp; Bible Teacher</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Book Details & CTA Column (Right on Desktop, Second on Mobile) */}
          <div className="order-2 lg:order-2 flex flex-col items-start lg:col-span-7">
            {/* Announcement Badge */}
            <Badge variant="outline" className="mb-6 gap-2 rounded-lg border-accent/40 bg-accent/10 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-foreground uppercase">
              <Sparkles className="size-3.5 text-accent" />
              <span>A GUIDE TO PURPOSEFUL LIVING</span>
            </Badge>

            {/* Editorial Serif Heading */}
            <h1 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl lg:leading-tight text-accent"><span className="text-primary">Rooted In Purchase: </span>From Inspiration To <span className="text-accent">Application</span></h1>

            {/* Author Attribution */}
            <p className="mt-2 text-base font-medium tracking-wide text-muted-foreground sm:text-lg">
              By <span className="text-foreground">Dustin Royer</span>
            </p>

            {/* Subheadline */}
            

            {/* Narrative Description with Expandable Container */}
            <div className={`overflow-hidden transition-[max-height] duration-300 max-w-2xl ${isExpanded ? "max-h-none" : "max-h-24 sm:max-h-28"}`}>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Are you searching for deeper clarity in your daily walk, spiritual calling, and relationship with God? <em>Rooted in Purpose</em> provides a compelling, faith-centered roadmap designed to anchor your heart in timeless biblical wisdom while navigating the complex demands of modern life.
              </p>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Written with pastoral warmth and practical insight, Dustin Royer walks you through transformative principles that empower you to discern God&apos;s voice, overcome seasons of uncertainty, and align your daily habits and decisions with eternal significance.
              </p>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
                Whether you are engaging in personal devotional reflection, family discipleship, or leading a small group study, this guide offers actionable steps, scriptural foundations, and real-world encouragement to help you build a resilient, purpose-driven spiritual legacy.
              </p>
            </div>

            <Button type="button" variant="link" onClick={() => setIsExpanded(prev => !prev)} className="mt-1 h-auto p-0 text-sm font-medium text-accent hover:underline cursor-pointer">
              {isExpanded ? "Read less" : "Read more"}
            </Button>

            {/* Feature Indicator Tags */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {featureTags.map((tag, index) => <div key={tag.label} data-index={index} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-1 text-xs font-medium text-foreground shadow-2xs">
                  <span className="size-2 rounded-lg bg-accent" />
                  <span>{tag.label}</span>
                </div>)}
              <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground pl-1">
                <Sparkles className="size-3.5 text-accent" />
                <span>Available for personal or group study</span>
              </div>
            </div>

            {/* Key Takeaways */}
            <Separator className="my-6 max-w-md bg-border/80" />
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                What You Will Discover Inside
              </h2>
              <div className="mt-4 space-y-3">
                {takeaways.map((item, index) => <div key={item.title} data-index={index} className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-lg bg-accent/20 text-accent">
                      <CheckCircle2 className="size-3.5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="group relative shadow-md transition-[color,background-color,border-color,box-shadow]">
                <Link to="https://a.co/d/0gC3g9aU" newTab className="inline-flex items-center gap-2">
                  <span>Buy on Amazon</span>
                  <ExternalLink className="size-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="group">
                <Link to="https://www.youtube.com/playlist?list=PLegS0uhpF7wxlvnAwdChlGtbDkSOOjE0l" newTab className="inline-flex items-center gap-2 font-medium">
                  <Youtube className="size-4" />
                  <span>View Study Resources</span>
                  <ExternalLink className="size-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}